import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reminderApi } from '../../services/api';

const ReminderLogList = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    days: 30,
    member_id: '',
    reminder_id: ''
  });
  const [reminders, setReminders] = useState([]);
  const [members, setMembers] = useState([]);
  
  // Fetch logs and filter options on component mount
  useEffect(() => {
    fetchData();
  }, []);
  
  // Fetch logs when filter changes
  useEffect(() => {
    fetchLogs();
  }, [filter]);
  
  // Function to fetch all necessary data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch reminders for filter dropdown
      const remindersResponse = await reminderApi.getAllReminders();
      setReminders(remindersResponse.data);
      
      // Fetch members for filter dropdown
      // We'll use the same API as in the ReminderTest component
      const membersResponse = await fetch('/api/members/');
      const membersData = await membersResponse.json();
      setMembers(membersData);
      
      // Fetch logs with default filter
      await fetchLogs();
      
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to fetch logs with current filter
  const fetchLogs = async () => {
    try {
      let response;
      
      if (filter.member_id) {
        response = await reminderApi.getLogsByMember(filter.member_id);
      } else if (filter.reminder_id) {
        response = await reminderApi.getLogsByReminder(filter.reminder_id);
      } else {
        response = await reminderApi.getRecentLogs(filter.days);
      }
      
      setLogs(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError('Failed to load logs. Please try again later.');
      setLogs([]);
    }
  };
  
  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // Reset other filters when one is selected
    if (name === 'member_id' && value) {
      setFilter({ member_id: value, reminder_id: '', days: 30 });
    } else if (name === 'reminder_id' && value) {
      setFilter({ reminder_id: value, member_id: '', days: 30 });
    } else if (name === 'days' && value) {
      setFilter({ days: parseInt(value), member_id: '', reminder_id: '' });
    } else {
      setFilter(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilter({ days: 30, member_id: '', reminder_id: '' });
  };
  
  // Helper function to format reminder type
  const formatType = (type) => {
    switch (type) {
      case 'email':
        return 'Email';
      case 'telegram':
        return 'Telegram';
      default:
        return type;
    }
  };
  
  // Helper function to get member name by ID
  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? (member.name || member.username) : 'Unknown';
  };
  
  // Helper function to get reminder name by ID
  const getReminderName = (reminderId) => {
    const reminder = reminders.find(r => r.id === reminderId);
    return reminder ? reminder.name : 'Unknown';
  };
  
  return (
    <div className="reminder-log-list">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Reminder Logs</h1>
        <Link to="/reminders" className="btn btn-outline-secondary">
          <i className="fas fa-arrow-left me-1"></i> Back to Reminders
        </Link>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="card shadow-sm mb-4">
        <div className="card-header">
          <h5 className="card-title mb-0">Filter Logs</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-3">
              <label htmlFor="days" className="form-label">Time Period</label>
              <select
                className="form-select"
                id="days"
                name="days"
                value={filter.days}
                onChange={handleFilterChange}
                disabled={filter.member_id || filter.reminder_id}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
            </div>
            
            <div className="col-md-4 mb-3">
              <label htmlFor="member_id" className="form-label">Filter by Member</label>
              <select
                className="form-select"
                id="member_id"
                name="member_id"
                value={filter.member_id}
                onChange={handleFilterChange}
              >
                <option value="">All Members</option>
                {members.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.name || member.username}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="col-md-4 mb-3">
              <label htmlFor="reminder_id" className="form-label">Filter by Reminder</label>
              <select
                className="form-select"
                id="reminder_id"
                name="reminder_id"
                value={filter.reminder_id}
                onChange={handleFilterChange}
              >
                <option value="">All Reminders</option>
                {reminders.map(reminder => (
                  <option key={reminder.id} value={reminder.id}>
                    {reminder.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="d-flex justify-content-end">
            <button
              className="btn btn-outline-secondary"
              onClick={resetFilters}
            >
              <i className="fas fa-times me-1"></i> Reset Filters
            </button>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : logs.length === 0 ? (
        <div className="alert alert-info" role="alert">
          No logs found for the selected filters.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Date</th>
                <th>Member</th>
                <th>Reminder</th>
                <th>Type</th>
                <th>Status</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td>{new Date(log.sent_at).toLocaleString()}</td>
                  <td>
                    <Link to={`/members/${log.member_id}`}>
                      {getMemberName(log.member_id)}
                    </Link>
                  </td>
                  <td>
                    <Link to={`/reminders/${log.reminder_setting_id}/edit`}>
                      {getReminderName(log.reminder_setting_id)}
                    </Link>
                  </td>
                  <td>{formatType(log.reminder_type)}</td>
                  <td>
                    <span className={`badge ${log.success ? 'bg-success' : 'bg-danger'}`}>
                      {log.success ? 'Success' : 'Failed'}
                    </span>
                  </td>
                  <td>
                    {log.error_message ? (
                      <span className="text-danger">{log.error_message}</span>
                    ) : (
                      <span className="text-muted">None</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReminderLogList;
